import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import get from 'lodash.get';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import { useCreateReturnLabel } from './hooks/use-create-return-label';
import { useEntitiesSalesOrder } from '../../../hooks/entities/selectors/sales-orders';
import { useSalesOrderIdContext } from '../../context/sales-order-id-context-provider';
import { useYupFormValidation } from '../../../validation/use-yup-form-validation';
import { validateAddressPromise } from '../../../address/address.api';
import { LogService } from '../../../services/log-service';
import { handleValidatedAddress } from '../../../components/create-label-content/create-label-helpers';
import { CSW_ENTER_RETURNS_MODE, exitReturnsMode } from '../../../store/csw/csw.actions';
import { shipToAndRecipientInfoSchema } from '../fulfillment-plan/sections/shipment-details/address/edit-shipto-modal/shipto-and-recipientinfo-schema';
import Global from '../../../store/global-selectors';
import { UNV, VER } from '../../../enums/address-verification-status';
import { ShipmentLabelType } from '../../../enums/shipment-label-type';
import { LABEL_OPTIONS } from '../../../constants/label-options';
import type { LabelOption } from '../../../constants/label-options';

type ReturnConfigContextType = {
  shipFrom: object;
  formState: object;
  labelType: string;
  isValid: boolean;
  emailAddressCSV: string;
  updateEmailCSV: (event: React.ChangeEvent<HTMLInputElement>) => void;
  validationErrors: object;
  isValidatingAddress: boolean;
  isShippingFeeWaived: boolean;
  isInternationalReturn: boolean;
  updateForm: (updates: object | ((prevState: object) => object)) => void;
  setLabelType: React.Dispatch<React.SetStateAction<string>>;
  resetFormState: () => void;
  validateAddress: () => Promise<void>;
  updateShipFrom: () => void;
  createReturnLabel: () => void;
  setIsShippingFeeWaived: React.Dispatch<React.SetStateAction<boolean>>;
  returnFulfillmentPlanId: string | undefined;
  hasEmailValidationError: boolean;
  selectedLabelOption: LabelOption;
  selectLabelOption: (option: LabelOption) => void;
};

export const ReturnConfigContext = createContext<ReturnConfigContextType | undefined>(undefined);

export const useReturnConfig = () => {
  const context = useContext(ReturnConfigContext);
  if (!context) {
    throw new Error('useReturnConfig must be used within a ReturnConfigProvider');
  }
  return context;
};

export const getReturnFulfillmentPlanId = (salesOrderId: string) => `return-${salesOrderId}`;

type ReturnConfigProviderProps = {
  returnId: string;
  customerAddress: object;
  onReturnConfirmed: (returnId: string, fulfillmentId: string) => void;
  children: React.ReactNode;
};

export const ReturnConfigProvider = ({ returnId, customerAddress, onReturnConfirmed, children }: ReturnConfigProviderProps) => {
  const salesOrderId = useSalesOrderIdContext();
  const salesOrder = useEntitiesSalesOrder(salesOrderId);

  const [shipFrom, setShipFrom] = useState(customerAddress);
  const [formState, setFormState] = useState(customerAddress);
  const [emailAddressCSV, setEmailAddressCSV] = useState(get(customerAddress, 'email', ''));
  const [isShippingFeeWaived, setIsShippingFeeWaived] = useState(false);
  const [isInternationalReturn, setIsInternationalReturn] = useState(false);
  const [isValidatingAddress, setIsValidatingAddress] = useAsyncSafeState(false);
  const [labelType, setLabelType] = useState(ShipmentLabelType.Label.key);
  const [selectedLabelOption, setSelectedLabelOption] = useState<LabelOption>(LABEL_OPTIONS.EMAIL);
  const isMountedRef = useRef(false);

  const returnFulfillmentPlanId = getReturnFulfillmentPlanId(salesOrderId);

  const dispatch = useDispatch();
  const { getState } = useStore();
  const sellerHomeCountry = Global.selectSellerHomeCountry(getState());

  const storeGuid: string = get(salesOrder, 'store.storeGuid', '');

  const resetFormState = useCallback(() => setFormState({ ...shipFrom }), [shipFrom]);

  const updateForm = useCallback((updates = {}) => {
    setFormState((prev) => ({
      ...prev,
      ...(typeof updates === 'function' ? updates(prev) || {} : updates),
      verificationStatus: UNV,
    }));
  }, []);

  const { errors: validationErrors, isValid } = useYupFormValidation({
    form: formState,
    schema: shipToAndRecipientInfoSchema,
  });

  const validateAddress = useCallback(async () => {
    setIsValidatingAddress(true);
    try {
      const response = await validateAddressPromise(formState);
      handleValidatedAddress(response[0], setFormState, (validatedAddress: object) => {
        setFormState((prev) => ({
          ...prev,
          ...validatedAddress,
          verificationStatus: VER,
        }));
      });
    } catch (error) {
      LogService.logInfo('Error validating address in OD -> Edit Enhanced Return Address Modal', error as object);
    }
    setIsValidatingAddress(false);
  }, [formState, setIsValidatingAddress]);

  const updateShipFrom = useCallback(() => {
    const formCountryCode = get(formState, 'countryCode', '');
    setIsInternationalReturn(formCountryCode !== sellerHomeCountry);
    setShipFrom(formState);
    dispatch({
      type: CSW_ENTER_RETURNS_MODE,
      salesOrderId,
      shipmentItemsForReturn: [], // TODO: Inspect its use case
      shipTo: formState,
    });
  }, [dispatch, formState, salesOrderId, sellerHomeCountry]);

  useEffect(() => {
    if (!isMountedRef.current) {
      updateShipFrom();
    }
    isMountedRef.current = true;
  }, [updateShipFrom]);

  const onReturnLabelCreated = useCallback(
    (fulfillmentId: string) => {
      try {
        dispatch(exitReturnsMode(returnFulfillmentPlanId));
        onReturnConfirmed(returnId, fulfillmentId);
      } catch (error) {
        LogService.logError(new Error('Error confirming return after label creation', { cause: error }));
      }
    },
    [returnId, returnFulfillmentPlanId, dispatch, onReturnConfirmed],
  );

  const updateEmailCSV = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setEmailAddressCSV(event.target.value);
  }, []);

  const selectLabelOption = useCallback((option: LabelOption) => {
    setSelectedLabelOption(option);
  }, []);

  const { createReturnLabel, hasEmailValidationError } = useCreateReturnLabel({
    returnId,
    fulfillmentPlanId: returnFulfillmentPlanId,
    storeGuid,
    labelType,
    emailAddressCSV,
    downloadLabelNow: selectedLabelOption === LABEL_OPTIONS.DOWNLOAD,
    onSuccess: onReturnLabelCreated,
  });

  const value = useMemo(() => {
    return {
      hasEmailValidationError,
      returnFulfillmentPlanId,
      isInternationalReturn,
      isShippingFeeWaived,
      shipFrom,
      emailAddressCSV,
      updateEmailCSV,
      resetFormState,
      updateForm,
      labelType,
      setLabelType,
      validationErrors,
      isValid,
      validateAddress,
      isValidatingAddress,
      formState,
      updateShipFrom,
      createReturnLabel,
      setIsShippingFeeWaived,
      selectedLabelOption,
      selectLabelOption,
    };
  }, [
    hasEmailValidationError,
    returnFulfillmentPlanId,
    isInternationalReturn,
    isShippingFeeWaived,
    shipFrom,
    emailAddressCSV,
    updateEmailCSV,
    resetFormState,
    updateForm,
    labelType,
    setLabelType,
    validationErrors,
    isValid,
    validateAddress,
    isValidatingAddress,
    formState,
    updateShipFrom,
    createReturnLabel,
    selectedLabelOption,
    selectLabelOption,
  ]);

  return <ReturnConfigContext.Provider value={value}>{children}</ReturnConfigContext.Provider>;
};
